import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AgentRole } from '@aiai/shared';
import type { AgentRole as AgentRoleT } from '@aiai/shared';
import { createIssue, updateIssue, runIssue } from '../api';
import { qk } from '../api/queryKeys';
import { errorMessage } from '../lib/api';
import { useWorkspacePath } from '../hooks/useWorkspacePath';
import { AGENT_TEMPLATES, type AgentTemplate } from '../lib/loopTemplates';
import { Callout } from './Callout';
import { FormField } from './FormField';
import { Modal } from './Modal';
import { Spinner } from './Spinner';

function TemplateCard({
  template,
  selected,
  onSelect,
}: {
  template: AgentTemplate | null;
  selected: boolean;
  onSelect: () => void;
}) {
  const isBlank = template === null;
  return (
    <div
      role="button"
      tabIndex={0}
      aria-pressed={selected}
      onClick={onSelect}
      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onSelect()}
      className={`cursor-pointer rounded-lg border p-3 transition-colors ${
        selected
          ? 'border-signal/60 bg-signal-soft'
          : 'border-line hover:border-line-strong hover:bg-surface-2'
      }`}
    >
      <p className="text-sm font-semibold text-ink">
        {isBlank ? 'Blank' : template.name}
      </p>
      <p className="mt-0.5 text-xs text-ink-muted">
        {isBlank ? 'Start from scratch with an empty prompt.' : template.description}
      </p>
      {!isBlank && (
        <span className="mt-1.5 inline-block rounded border border-line bg-surface-2 px-1.5 py-0.5 text-[0.6rem] font-mono text-ink-faint">
          {template.role === AgentRole.Planner ? 'planner' : 'worker'}
        </span>
      )}
    </div>
  );
}

export function RunTaskModal({
  open,
  onClose,
  projectId,
}: {
  open: boolean;
  onClose: () => void;
  projectId: string;
}) {
  const navigate = useNavigate();
  const { wp } = useWorkspacePath();
  const queryClient = useQueryClient();

  const [selectedTemplateId, setSelectedTemplateId] = useState<string | 'blank' | null>(null);
  const [title, setTitle] = useState('');
  const [prompt, setPrompt] = useState('');
  const [role, setRole] = useState<AgentRoleT>(AgentRole.Worker);
  const [formError, setFormError] = useState<string | null>(null);

  function applyTemplate(template: AgentTemplate | null) {
    if (template === null) {
      setSelectedTemplateId('blank');
      setTitle('');
      setPrompt('');
      setRole(AgentRole.Worker);
    } else {
      setSelectedTemplateId(template.id);
      setTitle(template.name);
      setPrompt(template.prompt);
      setRole(template.role);
    }
  }

  const runMutation = useMutation({
    mutationFn: async () => {
      const issue = await createIssue({
        projectId,
        title: title.trim(),
        body: prompt.trim(),
        pipeline: [role],
      });
      await updateIssue(issue.code ?? issue.id, { approved: true });
      const run = await runIssue(issue.code ?? issue.id);
      return { issueId: issue.id, runId: run.id };
    },
    onSuccess: ({ runId }) => {
      queryClient.invalidateQueries({ queryKey: qk.projectIssues(projectId) });
      onClose();
      navigate(wp(`/runs/${runId}`));
    },
    onError: (err) => setFormError(errorMessage(err, 'Failed to start task')),
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setFormError(null);
    if (!title.trim()) {
      setFormError('Title is required.');
      return;
    }
    if (!prompt.trim() || prompt.trim().length < 10) {
      setFormError('Prompt must be at least 10 characters.');
      return;
    }
    runMutation.mutate();
  }

  function handleClose() {
    if (runMutation.isPending) return;
    onClose();
    setSelectedTemplateId(null);
    setTitle('');
    setPrompt('');
    setRole(AgentRole.Worker);
    setFormError(null);
  }

  const busy = runMutation.isPending;

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Run a task"
      widthClass="max-w-2xl"
      footer={
        <>
          <button type="button" className="btn-secondary" onClick={handleClose} disabled={busy}>
            Cancel
          </button>
          <button
            type="submit"
            form="run-task-form"
            className="btn-primary"
            disabled={busy}
          >
            {busy && <Spinner className="h-3.5 w-3.5" />}
            Run task
          </button>
        </>
      }
    >
      <div className="space-y-5">
        {/* Template gallery */}
        <div className="space-y-2">
          <p className="text-xs font-semibold text-ink-muted uppercase tracking-wide">
            Start from a template
          </p>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <TemplateCard
              template={null}
              selected={selectedTemplateId === 'blank'}
              onSelect={() => applyTemplate(null)}
            />
            {AGENT_TEMPLATES.map((t) => (
              <TemplateCard
                key={t.id}
                template={t}
                selected={selectedTemplateId === t.id}
                onSelect={() => applyTemplate(t)}
              />
            ))}
          </div>
        </div>

        <form id="run-task-form" onSubmit={handleSubmit} className="space-y-4">
          <FormField label="Title">
            <input
              type="text"
              className="input w-full"
              placeholder="e.g. Audit security of the auth module"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              disabled={busy}
            />
          </FormField>

          <FormField
            label="Role"
            hint={
              role === AgentRole.Planner
                ? 'Planner: read-only — investigates and files issues, never edits code.'
                : 'Worker: full toolkit — can edit code and open a pull request.'
            }
          >
            <select
              className="input w-full"
              value={role}
              onChange={(e) => setRole(e.target.value as AgentRoleT)}
              disabled={busy}
            >
              <option value={AgentRole.Worker}>Worker — writes code (→ PR)</option>
              <option value={AgentRole.Planner}>Planner — read-only, never writes code</option>
            </select>
          </FormField>

          <FormField label="Prompt">
            <textarea
              className="input min-h-40 w-full font-mono text-xs leading-relaxed"
              placeholder="Describe the task for the agent…"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              disabled={busy}
            />
          </FormField>

          {formError && <Callout tone="danger">{formError}</Callout>}
        </form>
      </div>
    </Modal>
  );
}
